import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalized } from "@/hooks/useLocalized";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import axios from "axios";
import { API } from "@/App";
import logger from "@/utils/logger";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import HeroSection from "./home/HeroSection";
import FeaturesSection from "./home/FeaturesSection";
import TestimonialsCarousel from "./home/TestimonialsCarousel";
import CTASection from "./home/CTASection";

const HomePage = () => {
  const { t } = useTranslation();
  const { l } = useLocalized();
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  const featuresRef = useScrollReveal();
  const packagesRef = useScrollReveal();
  const testimonialsRef = useScrollReveal();
  const ctaRef = useScrollReveal();

  const fetchData = useCallback(async () => {
    try {
      const [packagesRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/packages`),
        axios.get(`${API}/testimonials?featured=true`),
      ]);
      setPackages(packagesRes.data.slice(0, 4));
      setTestimonials(testimonialsRes.data.slice(0, 6));
    } catch (error) {
      logger.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div data-testid="home-page">
      <HeroSection />

      <FeaturesSection sectionRef={featuresRef} />

      {/* Featured Packages Section */}
      <section
        data-testid="packages-section"
        className="py-20 md:py-32 bg-white"
        ref={packagesRef}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="section-header reveal">
            <span className="text-[#E07A5F] font-medium tracking-wider uppercase text-sm">
              {t("home.packages.badge")}
            </span>
            <h2 className="mt-3">{t("home.packages.title")}</h2>
            <p>{t("home.packages.subtitle")}</p>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {packages.map((pkg, idx) => (
                <Card
                  key={pkg.id}
                  className={`reveal stagger-${idx + 1} package-card overflow-hidden card-lift`}
                  data-testid={`package-card-${idx}`}
                >
                  <div className="img-hover-zoom h-56">
                    <img
                      src={
                        pkg.image_url ||
                        "https://images.unsplash.com/photo-1665332195309-9d75071138f0?w=600"
                      }
                      alt={l(pkg, "name")}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6">
                    <span className="badge badge-primary text-xs mb-3">
                      {pkg.package_type}
                    </span>
                    <h3 className="text-2xl font-semibold text-[#3D405B] mb-2">
                      {l(pkg, "name")}
                    </h3>
                    <p className="text-[#6B705C] text-sm mb-4 line-clamp-2">
                      {l(pkg, "description")}
                    </p>
                    <div className="flex items-end justify-end">
                      <Link to="/booking">
                        <Button
                          size="sm"
                          className="bg-[#2D6A4F] hover:bg-[#245a42] text-white rounded-full btn-hover-pulse"
                          data-testid={`book-package-${idx}`}
                        >
                          {t("home.packages.bookNow")}
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="text-center mt-12 reveal">
            <Link to="/menu">
              <Button
                variant="outline"
                className="btn-secondary btn-hover-pulse"
                data-testid="view-all-packages-btn"
              >
                {t("home.packages.viewAll")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <TestimonialsCarousel
        testimonials={testimonials}
        loading={loading}
        sectionRef={testimonialsRef}
      />

      <CTASection sectionRef={ctaRef} />
    </div>
  );
};

export default HomePage;
